"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Box, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function ShippingNewMechanicalBulls() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="shipping-new-mechanical-bulls" data-nav="light" className="relative w-full bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative bg-card text-card-foreground border-2 border-border p-6 sm:p-10 md:p-14 shadow-sm">
          {/* Decorative Corner Hardware Marks */}
          <div className="absolute top-2 left-2 size-2 border-t-2 border-l-2 border-muted-foreground/40 pointer-events-none" />
          <div className="absolute top-2 right-2 size-2 border-t-2 border-r-2 border-muted-foreground/40 pointer-events-none" />
          <div className="absolute bottom-2 left-2 size-2 border-b-2 border-l-2 border-muted-foreground/40 pointer-events-none" />
          <div className="absolute bottom-2 right-2 size-2 border-b-2 border-r-2 border-muted-foreground/40 pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Primary Content Column */}
            <div className="lg:col-span-8 space-y-6">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground leading-none">
                Shipping New Mechanical Bulls
              </h2>

              <div className="space-y-4 text-foreground/90 font-sans text-base sm:text-lg leading-relaxed">
                <p>
                  New mechanical bulls are large pieces of equipment, so delivery requires more planning than a normal parcel shipment. Delivery arrangements can depend on the machine&apos;s packed dimensions, weight, shipping origin, destination and unloading requirements.
                </p>
                <p className="text-muted-foreground font-sans text-sm sm:text-base leading-relaxed">
                  Before confirming a purchase, make sure the delivery location can receive the equipment and that any special unloading requirements are understood in advance.
                </p>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button asChild size="lg" className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground font-mono font-semibold tracking-wider uppercase px-8 h-12 text-sm shadow-xs transition-colors">
                  <Link to="/ShippingWarrantyAndOrderingPage">
                    <span>VIEW SHIPPING INFORMATION</span>
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Technical Checklist Sidebar Plate */}
            <div className="lg:col-span-4 bg-muted/40 border border-border p-5 space-y-4">
              <div className="border-b border-border/80 pb-2">
                <p className="font-mono text-xs font-bold text-foreground uppercase tracking-wide">
                  BEFORE DELIVERY
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Box className="size-4 text-primary shrink-0 mt-0.5" />
                  <div className="text-xs text-muted-foreground">
                    <strong className="block text-foreground font-medium font-sans">Unloading Requirements</strong>
                    Depending on the shipment, unloading equipment or special receiving arrangements may be required. Confirm the requirements for the exact machine before delivery.
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <div className="text-xs text-muted-foreground">
                    <strong className="block text-foreground font-medium font-sans">Delivery Access</strong>
                    Check that the delivery location has suitable access for the freight service being used. Access requirements can vary by shipment and destination.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}